import { percent } from "@amcharts/amcharts5";
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import { IPieChartSettings, PieChart, PieSeries } from "@amcharts/amcharts5/percent";
import { XYChart } from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn, map, pick } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

const TOP_DATA_LABEL_KEY = "__isTopDataLabel";
const EXTRA_LABEL_PADDING_DEFAULT = 120;
const EXTRA_LABEL_PADDING_EXTRA = 120;

export default class extends BaseChart {
  tooltipValuesField = "tooltipValues";
  private dataLabelsTopLimit?: number;

  initChart() {
    this.root.container.children.clear();
    this.createChart();

    const series = [this.settings.displayMetric, this.settings.displayMetricSecondary];
    series.forEach((name, index) => {
      this.createSeries(name, index);
    });

    this.showLegend(
      map(this.data, this.settings.decompositionMetric) as string[],
      (this.chart as XYChart).series.values[0].dataItems as LegendData,
      false,
      false
    );
  }

  protected createChart() {
    const holeSize = Number(this.settings.holeSize);
    const innerRadius = Number.isNaN(holeSize) ? this.settings.holeSize.default : holeSize;

    const chartSettings: IPieChartSettings = {
      radius: percent(70),
      y: percent(0),
      layout: this.layout,
    };

    if (this.settings?.showDataLabels) {
      const extraAir = this.settings.dataLabelsShowPercent ? EXTRA_LABEL_PADDING_EXTRA : EXTRA_LABEL_PADDING_DEFAULT;

      chartSettings.paddingLeft = extraAir;
      chartSettings.paddingRight = extraAir;
    }

    if (innerRadius !== 0) {
      chartSettings.innerRadius = percent(innerRadius);
    }

    this.chart = this.root.container.children.push(PieChart.new(this.root, chartSettings));
  }

  protected createSeries(name: string, index: number) {
    const series = (this.chart as PieChart).series.push(
      PieSeries.new(this.root, {
        name,
        valueField: name,
        categoryField: this.settings.decompositionMetric,
      })
    );

    this.createTooltip(series as TooltipSeries, (target) => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = target?.dataItem?.dataContext;

      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          if (key === name || key === this.settings.decompositionMetric || key === this.tooltipValuesField) {
            param.push({ key, value });
          }
        });
      }
      return param;
    });

    series.get("colors")?.set("colors", this.palette);

    const scaleValue = 0.9 + index / 10;
    series.slices.template.setAll({
      scale: scaleValue,
    });

    const hoverScaleDiff = 0.02;
    series.slices.template.states.create("hover", { scale: scaleValue + hoverScaleDiff });

    const OUTER_RING_INDEX = 1;
    const metricKey = index === OUTER_RING_INDEX ? this.settings.displayMetric : this.settings.displayMetricSecondary;
    const metricFormatter = this.formatters?.[metricKey];
    const formatterConfig = this.getDataLabelsFormatterConfig();

    if (this.settings.showDataLabels && index === OUTER_RING_INDEX) {
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      series.set("alignLabels", true);

      series.labels.template.setAll({
        visible: true,
        forceHidden: false,
        populateText: false,
        fontSize: labelFontSize,
      });

      series.labels.template.adapters.add("text", (_text, target: am5.RadialLabel) => {
        const ctx = target.dataItem?.dataContext as Record<string, unknown> | undefined;
        if (!ctx) {
          return "";
        }

        if (this.dataLabelsTopLimit && ctx[TOP_DATA_LABEL_KEY] !== true) {
          return "";
        }

        const rawValue = Number(ctx[name]);

        if (!Number.isFinite(rawValue)) {
          return "";
        }

        const formattedValue = metricFormatter
          ? metricFormatter(rawValue, formatterConfig)
          : this.root.numberFormatter.format(rawValue, this.getMetricFormatter(metricKey, formatterConfig));

        if (!formattedValue) {
          return "";
        }

        let suffix = "";
        const pieDataItem = target.dataItem as am5.DataItem<am5percent.IPieSeriesDataItem>;
        const percentValue = pieDataItem?.get("valuePercentTotal");

        if (this.shouldShowPercentLabels() && typeof percentValue === "number" && Number.isFinite(percentValue)) {
          const decimals = this.getDataLabelsDecimals();
          const precision = decimals !== undefined ? decimals : 1;
          const clamped = Math.min(Math.max(precision, 0), 6);
          suffix = ` (${percentValue.toFixed(clamped)}%)`;
        }

        return `${formattedValue}${suffix}`;
      });

      series.ticks.template.setAll({
        visible: true,
        forceHidden: false,
      });

      series.ticks.template.adapters.add("visible", (_visible, target) => {
        const ctx = target.dataItem?.dataContext as Record<string, unknown> | undefined;
        if (this.dataLabelsTopLimit && ctx?.[TOP_DATA_LABEL_KEY] !== true) {
          return false;
        }

        return true;
      });
    } else {
      series.labels.template.setAll({ visible: false, forceHidden: true });
      series.ticks.template.setAll({ visible: false, forceHidden: true });
    }

    series.data.setAll(this.data);
  }

  protected mapData(data: ChartDataRecord[]) {
    if (!data || !Object.keys(data)) {
      return [];
    }

    const primaryMetric = this.settings.displayMetric;
    const secondaryMetric = this.settings.displayMetricSecondary;
    const categoryField = this.settings.decompositionMetric;

    if (!categoryField || !primaryMetric || !secondaryMetric) {
      return [];
    }

    let preparedData: ChartDataRecord[] = cloneDeep(data);

    if (
      (this.module === PLAN_MODULE || this.isDualMode) &&
      (!this.settings?.useExploreData || this.isDualMode) &&
      this.settings?.showItems &&
      Number.isFinite(this.settings?.showItems)
    ) {
      preparedData = this.prepareTopPlanModuleData(preparedData, categoryField, primaryMetric, this.settings.showItems);
    }

    preparedData = preparedData.map((item) => pick(item, [categoryField, primaryMetric, secondaryMetric]));

    preparedData.sort((a, b) => (b[primaryMetric] as number) - (a[primaryMetric] as number));

    let resultData: ChartDataRecord[] = preparedData;
    resultData = this.showItems(resultData, categoryField, primaryMetric);

    this.dataLabelsTopLimit = this.getDataLabelsTopLimit();

    resultData.forEach((item, index) => {
      if (this.dataLabelsTopLimit) {
        (item as Record<string, unknown>)[TOP_DATA_LABEL_KEY] = index < this.dataLabelsTopLimit;
      }
    });

    return resultData.map((item) => ({
      ...item,
      tooltipValues: {
        [categoryField]: this.formatters[categoryField](item[categoryField]),
        [primaryMetric]: this.formatters[primaryMetric](item[primaryMetric]),
        [secondaryMetric]: this.formatters[secondaryMetric](item[secondaryMetric]),
      },
    }));
  }

  private shouldShowPercentLabels(): boolean {
    return Boolean(this.settings?.dataLabelsShowPercent);
  }
}
